import { getTotalOdd } from "src/rules/calculation/odd";
import { getPossibleWin, getSystemMinPossibleWin } from "src/rules/calculation/possibleWin";
import { getExpressBonus } from "src/rules/calculation/bonus";
import { IPartnerConfig, IBonusRule, IExpressBonus, ICalculationResult, ICurrencyConfig } from "./src/constants/interfaces";
import { IFreeBet, ISelection } from "src/constants/interfaces/bet";
import { IClientConfig } from "src/client-config/config";
import { getTaxedStake, getTaxForReturnedOrProfit } from "./src/rules/calculation/tax";
import { getTotalWin } from "src/rules/calculation/totalWin";
import { BetTypeEnum } from "src/constants/enums/betType";
import { BetCalculationsEnum } from "src/constants/enums/betCalculations";
import { CalculationsHandlersType } from "src/constants/types";
import { getEstimatedReturn } from "src/rules/calculation/estimatedReturn";
import { roundAmount } from "src/rules/calculation/helpers";

const calculateAmountRate = (amount: number, config: IClientConfig, currencyConfig?: ICurrencyConfig) => {
  if (!config.showAdditionalCoin) return 0;

  if (currencyConfig && currencyConfig.name === 'BGN') {
    return roundAmount(((amount * 1000000) / (config.additionalCoinRate * 1000000)), currencyConfig);
  }

  if (currencyConfig && currencyConfig.name === 'EUR') {
    return roundAmount(((amount * config.additionalCoinRate)), currencyConfig);
  }
}

const handleCalculation = (
  detail: BetCalculationsEnum,
  config: IClientConfig,
  partnerConfig: IPartnerConfig | undefined,
  stake: number,
  events: ISelection[],
  betType: BetTypeEnum,
  bonusRule: IBonusRule[] | undefined,
  systemCombinationCount: number,
  selectedFreeBet?: IFreeBet,
  currencyConfig?: ICurrencyConfig,
) => {
  const handlerMap: CalculationsHandlersType = {
    [BetCalculationsEnum.TaxOnStake]: () => getTaxedStake(partnerConfig, stake, currencyConfig),
    [BetCalculationsEnum.TotalOdd]: () => getTotalOdd(config, partnerConfig, betType, events),
    [BetCalculationsEnum.PossibleWin]: () =>
      getPossibleWin(
        config,
        betType,
        stake,
        events,
        systemCombinationCount,
        partnerConfig,
        selectedFreeBet,
        currencyConfig,
      ),
    [BetCalculationsEnum.MinPossibleWin]: () =>
      getSystemMinPossibleWin(stake, events, systemCombinationCount, partnerConfig, currencyConfig),
    [BetCalculationsEnum.Bonus]: () => getExpressBonus(config, stake, events, bonusRule, partnerConfig, currencyConfig),
    [BetCalculationsEnum.EstimatedReturn]: () =>
      getEstimatedReturn(config, betType, stake, events, bonusRule, partnerConfig, currencyConfig),
    [BetCalculationsEnum.TaxForReturnOrProfit]: () =>
      getTaxForReturnedOrProfit(config, stake, events, betType, partnerConfig, currencyConfig, bonusRule),
    [BetCalculationsEnum.TotalWin]: () =>
      getTotalWin(config, stake, events, betType, systemCombinationCount, bonusRule, partnerConfig, currencyConfig),
  };

  return handlerMap[detail]?.();
};

type BetCalculationArgs = {
  config: IClientConfig;
  partnerConfig?: IPartnerConfig | undefined;
  stake?: number;
  events?: ISelection[];
  betType?: BetTypeEnum;
  bonusRule?: IBonusRule[] | undefined;
  systemCombinationCount?: number;
  oddsFormat?: string;
  selectedFreeBet?: IFreeBet;
  currencyConfig?: ICurrencyConfig;
};

const getBetCalculations = <TDetails extends BetCalculationsEnum[]>(details: TDetails, args: BetCalculationArgs) => {
  const defaultArgs = {
    stake: 0,
    events: [],
    betType: BetTypeEnum.Multiple,
    systemCombinationCount: 0,
  };
  const { config, partnerConfig, stake, events, betType, bonusRule, systemCombinationCount, selectedFreeBet, currencyConfig } = {
    ...defaultArgs,
    ...args,
  };

  const result: Partial<{ [key in BetCalculationsEnum]: number | null | IExpressBonus }> = {};

  for (const detail of details) {
    result[detail] = handleCalculation(
      detail,
      config,
      partnerConfig,
      stake,
      events,
      betType,
      bonusRule,
      systemCombinationCount,
      selectedFreeBet,
      currencyConfig,
    );
  }

  return result as Pick<ICalculationResult, TDetails[number]>;
};

export { getBetCalculations, calculateAmountRate, BetCalculationsEnum };
